import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@meno/ui/contexts/AuthContext'
import { useWebSocket, type WsMessage } from '@meno/ui/hooks/useWebSocket'
import { getTasks, clearTasks as clearTasksApi, type Task } from '@meno/ui/lib/api'

// ── Client-side ephemeral upload task ────────────────────────────────────────
// Uploads stream bytes straight from the browser, so only the client knows the
// live %. These live here (not on the server) until the upload settles; the
// server records its own persisted `upload` task that takes over in the list.
export type UploadTask = {
  id: string
  client: true
  type: 'upload'
  target_label: string
  status: 'running' | 'done' | 'failed'
  progress: number
  kind?: string
  created_at: string
}

export type AnyTask = Task | UploadTask

export function isUploadTask(t: AnyTask): t is UploadTask {
  return (t as UploadTask).client === true
}

// Every publish-kind task type belongs here — anything missing silently
// disappears from the live "Running now" panel/bell dropdown while still
// showing correctly in the main Activity Center list (which filters
// separately, see activityTypes.ts). This previously missed
// publish_media/publish_menu (fixed) and was found missing
// publish_stop_screen/publish_transition too — keep this in sync with
// activityTypes.ts's PUBLISH_TYPE_META keys.
const PUBLISH_TYPES = new Set<Task['type']>([
  'publish_playlist', 'publish_layout', 'publish_media', 'publish_menu',
  'publish_audio', 'publish_audio_playlist', 'publish_stop_screen', 'publish_transition',
])

// A pending task whose scheduled window hasn't opened yet (a future-dated
// publish, or a removal task before its end_at). It's queued for a later
// moment, not in flight, so it belongs in the feed as a "Scheduled" row rather
// than the live "Running now" panel.
function scheduledNotOpen(t: Task): boolean {
  if (t.status !== 'pending') return false
  const w = (t.meta as { window?: { start_at?: string | null; end_at?: string | null } } | null | undefined)?.window
  if (!w) return false
  const at = t.type === 'content_expire' ? w.end_at : w.start_at
  return !!at && new Date(at).getTime() > Date.now()
}

type RunUploadOpts<T> = {
  /** Display label (usually the file name). */
  label: string
  /** Tag for the type icon, e.g. "media" | "audio". */
  kind?: string
  /** Performs the upload; call `onProgress(pct)` to drive the live bar. */
  upload: (onProgress: (pct: number) => void) => Promise<T>
}

type TaskCenterValue = {
  /** Persisted server tasks, newest first. */
  tasks: Task[]
  /** In-flight uploads + publishes with screens still pending — for the panel. */
  runningTasks: AnyTask[]
  /** Count of incomplete work, for the top-bar badge. */
  activeCount: number
  refresh: () => void
  runUpload: <T>(opts: RunUploadOpts<T>) => Promise<T>
  /** Clears finished (done/failed) tasks server-side; keeps in-flight work. */
  clearFinished: () => Promise<number>
}

const TaskCenterContext = createContext<TaskCenterValue | null>(null)

const rid = () =>
  (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)

/** Subscribes to the realtime hub only while mounted (i.e. only when authed). */
function TaskWsBridge({ onMessage }: { onMessage: (msg: WsMessage) => void }) {
  useWebSocket(onMessage)
  return null
}

export function TaskCenterProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [uploads, setUploads] = useState<UploadTask[]>([])

  const refresh = useCallback(() => {
    if (!user) return
    getTasks({ limit: 100 })
      .then(r => setTasks(r.data))
      .catch(() => { /* feed is best-effort; ignore */ })
  }, [user])

  // Seed on login; clear on logout so a different account never sees stale rows.
  useEffect(() => {
    if (user) refresh()
    else { setTasks([]); setUploads([]) }
  }, [user, refresh])

  const onMessage = useCallback((msg: WsMessage) => {
    if (msg.event !== 'task_update') return
    const task = (msg.data as { task?: Task })?.task
    if (!task) return
    setTasks(prev => {
      const idx = prev.findIndex(t => t.id === task.id)
      if (idx === -1) return [task, ...prev]
      const next = prev.slice()
      next[idx] = task
      return next
    })
  }, [])

  const runUpload = useCallback(<T,>(opts: RunUploadOpts<T>): Promise<T> => {
    const id = `upload-${rid()}`
    setUploads(prev => [
      { id, client: true, type: 'upload', target_label: opts.label, status: 'running', progress: 0, kind: opts.kind, created_at: new Date().toISOString() },
      ...prev,
    ])
    const onProgress = (pct: number) =>
      setUploads(prev => prev.map(u => (u.id === id ? { ...u, progress: pct } : u)))
    const drop = (delay: number) =>
      setTimeout(() => setUploads(prev => prev.filter(u => u.id !== id)), delay)

    return opts.upload(onProgress).then(
      (res) => {
        setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'done', progress: 100 } : u)))
        // The server-persisted `upload` task (delivered via task_update / refresh)
        // takes its place in the list once it lands.
        refresh()
        drop(1200)
        return res
      },
      (err) => {
        setUploads(prev => prev.map(u => (u.id === id ? { ...u, status: 'failed' } : u)))
        drop(4000)
        throw err
      },
    )
  }, [refresh])

  // Clear finished activity. Optimistically drops done/failed rows locally so
  // the feed empties instantly, then reconciles with the server count.
  const clearFinished = useCallback(async (): Promise<number> => {
    setTasks(prev => prev.filter(t => t.status === 'pending' || t.status === 'running'))
    try {
      const { data } = await clearTasksApi()
      return data.deleted
    } catch {
      refresh()  // restore the true state if the delete failed
      throw new Error('clear-failed')
    }
  }, [refresh])

  const runningTasks = useMemo<AnyTask[]>(() => {
    const runningUploads = uploads.filter(u => u.status !== 'done')
    const pendingPublishes = tasks.filter(
      t => PUBLISH_TYPES.has(t.type)
        && (t.status === 'pending' || t.status === 'running')
        && !scheduledNotOpen(t),
    )
    return [...runningUploads, ...pendingPublishes]
  }, [uploads, tasks])

  const activeCount = useMemo(() => {
    const runningUploads = uploads.filter(u => u.status === 'running').length
    const incompleteServer = tasks.filter(t => t.status === 'pending' || t.status === 'running').length
    return runningUploads + incompleteServer
  }, [uploads, tasks])

  const value = useMemo<TaskCenterValue>(
    () => ({ tasks, runningTasks, activeCount, refresh, runUpload, clearFinished }),
    [tasks, runningTasks, activeCount, refresh, runUpload, clearFinished],
  )

  return (
    <TaskCenterContext.Provider value={value}>
      {user && <TaskWsBridge onMessage={onMessage} />}
      {children}
    </TaskCenterContext.Provider>
  )
}

export function useTaskCenter(): TaskCenterValue {
  const ctx = useContext(TaskCenterContext)
  if (!ctx) throw new Error('useTaskCenter must be used within a TaskCenterProvider')
  return ctx
}
